import React, { useId, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { monotonePath } from "common/NewLook/monotonePath.ts";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import {
  canDrawSparkline,
  resampleSeries,
} from "features/bots/NewLook/newLookBotUtils";

interface NewLookAreaChartProps {
  /** The series, oldest first (e.g. accumulated profit). */
  values: number[];
  /** The band's height; its width follows the container. */
  height?: number;
  /** Overrides the direction (by default the series' own: end vs start). */
  isUp?: boolean;
  /** The dot on the latest value (default true). */
  showEndDot?: boolean;
  className?: string;
}

/** Drawing units across (the SVG stretches to the container's width). */
const WIDTH = 1000;
/** Room above the highest point for the end dot's ring. */
const PAD_TOP = 5;
const PAD_BOTTOM = 2;

/**
 * New look: a full-width band chart of a series (a public bot's accumulated
 * profit) — a smooth 2px line coloured by the period's result (green up,
 * red down, it is P&L), a soft fill under it, a dot on the latest value and a
 * dotted hairline at zero when the series crosses zero. The SVG stretches with the card
 * (the stroke keeps its width); the dot is an HTML element, so it stays
 * round. Draws nothing from fewer than three points or a flat series.
 */
const NewLookAreaChart = ({
  values: rawValues,
  height = 64,
  isUp: isUpOverride,
  showEndDot = true,
  className,
}: NewLookAreaChartProps) => {
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  // useId() may contain characters that aren't valid in url(#…).
  const gradientId = `area-${useId().replace(/[^\w-]/g, "")}`;
  const drawable = canDrawSparkline(rawValues);

  const shape = useMemo(() => {
    if (!drawable) return undefined;
    const values = resampleSeries(rawValues);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;
    if (!range) return undefined;
    const y = (value: number) =>
      PAD_TOP + (1 - (value - min) / range) * (height - PAD_TOP - PAD_BOTTOM);
    const points = values.map(
      (value, index) =>
        [(index / (values.length - 1)) * WIDTH, y(value)] as [number, number]
    );
    const line = monotonePath(points);
    const end = points[points.length - 1];
    return {
      line,
      area: `${line} L${WIDTH} ${height} L0 ${height} Z`,
      endTop: (end[1] / height) * 100,
      zeroY: min < 0 && max > 0 ? y(0) : undefined,
      isUp: values[values.length - 1] >= values[0],
    };
  }, [drawable, rawValues, height]);

  if (!shape) return null;
  const color = isUpOverride ?? shape.isUp ? tokens.up : tokens.down;

  return (
    <Band className={className} style={{ height }} aria-hidden={true}>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${WIDTH} ${height}`}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop
              offset="0%"
              stopColor={color}
              stopOpacity={theme.isDarkMode ? 0.2 : 0.14}
            />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        {shape.zeroY !== undefined && (
          <line
            x1={0}
            x2={WIDTH}
            y1={shape.zeroY}
            y2={shape.zeroY}
            stroke={tokens.hairlineStrong}
            strokeWidth={1}
            strokeDasharray="2 4"
            vectorEffect="non-scaling-stroke"
          />
        )}
        <path d={shape.area} fill={`url(#${gradientId})`} />
        <path
          d={shape.line}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {showEndDot && (
        <EndDot style={{ top: `${shape.endTop}%`, background: color }} />
      )}
    </Band>
  );
};

export default React.memo(NewLookAreaChart);

const Band = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  svg {
    display: block;
    overflow: visible;
  }
`;
/** 5px, with a 2px ring in the card's colour. */
const EndDot = styled.span`
  position: absolute;
  right: 0;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  transform: translate(50%, -50%);
  box-shadow: 0 0 0 2px ${(p) => getNewLookTokens(p.theme).panel};
`;
